import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FiAiChatService } from '@fi-shared/fi-ai-chat/fi-ai-chat.service';

@Injectable({ providedIn: 'root' })
export class FiAiChatHelperService {
    // Layout constants — keep these in sync with fi-ai-chat.component.scss.
    // Single source of truth for any component that needs to position itself relative to the AI panel.
    static readonly PANEL_WIDTH = 400;
    static readonly PANEL_DEFAULT_RIGHT = 25;
    static readonly PANEL_GAP = 15;
    private _isOpen$ = new BehaviorSubject<boolean>(false);
    private _activeConversationId$ = new BehaviorSubject<string | null>(null);
    private _isAvailable$ = new BehaviorSubject<boolean>(false);
    private availabilityPromise: Promise<boolean> | null = null;

    constructor(private fiAiChatService: FiAiChatService) {}

    get isOpen$() {
        return this._isOpen$.asObservable();
    }

    get isOpen(): boolean {
        return this._isOpen$.value;
    }

    get activeConversationId$() {
        return this._activeConversationId$.asObservable();
    }

    get activeConversationId(): string | null {
        return this._activeConversationId$.value;
    }

    get isAvailable$() {
        return this._isAvailable$.asObservable();
    }

    get isAvailable(): boolean {
        return this._isAvailable$.value;
    }

    open(): void {
        if (!this._isAvailable$.value) return;
        this._isOpen$.next(true);
    }

    close(): void {
        this._isOpen$.next(false);
    }

    toggle(): void {
        if (!this._isAvailable$.value) return;
        this._isOpen$.next(!this._isOpen$.value);
    }

    setActiveConversationId(conversationId: string | null): void {
        this._activeConversationId$.next(conversationId);
    }

    ensureAvailabilityLoaded(): Promise<boolean> {
        if (this.availabilityPromise) return this.availabilityPromise;
        this.availabilityPromise = this.fiAiChatService
            .getAvailability()
            .then((response) => {
                const isAvailable = !!response?.IsAvailable;
                this._isAvailable$.next(isAvailable);
                return isAvailable;
            })
            .catch(() => {
                this._isAvailable$.next(false);
                this.availabilityPromise = null;
                return false;
            });
        return this.availabilityPromise;
    }
}
